import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  NotificationsActive as NotificationsIcon,
} from '@mui/icons-material';
import api from '../../services/api';

interface RemindersModalProps {
  open: boolean;
  onClose: () => void;
}

const RemindersModal: React.FC<RemindersModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    pilot_name: '',
    phone: '',
  });
  const [loginData, setLoginData] = useState({
    email: '',
    pin: '',
  });
  const [showLogin, setShowLogin] = useState(false);

  const authenticateUser = useCallback(async (email: string, pin: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post('/reminders/login', {
        email,
        pin
      });
      
      if (response.data.success) {
        // Store credentials for session
        localStorage.setItem('userEmail', email);
        localStorage.setItem('userPin', pin);
        
        // Navigate to the equipment dashboard
        onClose();
        navigate('/app/reminders/dashboard');
      }
    } catch (err: any) {
      console.error('Authentication error:', err);
      if (err.response?.status === 401) {
        setError(t('reminders.errors.invalidPin'));
      } else if (err.response?.status === 404) {
        setError(t('reminders.errors.subscriptionNotFound'));
      } else {
        setError(t('reminders.errors.authFailed'));
      }
      // Clear stored PIN if authentication fails
      localStorage.removeItem('userPin');
    } finally {
      setLoading(false);
    }
  }, [t, navigate, onClose]);

  useEffect(() => {
    if (open) {
      // Check if user has stored credentials
      const storedEmail = localStorage.getItem('userEmail');
      const storedPin = localStorage.getItem('userPin');
      
      if (storedEmail && storedPin) {
        // User is already authenticated, redirect to dashboard
        onClose();
        navigate('/app/reminders/dashboard');
      } else if (storedEmail) {
        // Have email but no PIN - show login
        setLoginData(prev => ({ ...prev, email: storedEmail }));
        setShowLogin(true);
      } else {
        // No stored credentials - show subscription form
        setShowLogin(false);
      }
    } else {
      // Reset state when modal closes
      setShowLogin(false);
      setError(null);
    }
  }, [open, navigate, onClose]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    await authenticateUser(loginData.email, loginData.pin);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError(t('common.emailRequired'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const requestData: any = {
        email: formData.email,
        notification_preferences: {
          email_enabled: true,
          reminder_days: [30, 14, 7, 1],
        },
        service_tier: 'free'
      };

      if (formData.pilot_name) {
        requestData.pilot_name = formData.pilot_name;
      }

      if (formData.phone) {
        requestData.phone = formData.phone;
      }

      const response = await api.post('/reminders/subscribe', requestData);

      if (response.data) {
        setSuccess(true);
        localStorage.setItem('userEmail', formData.email);
        
        // Show success message then redirect
        setTimeout(() => {
          onClose();
          navigate('/app/reminders/dashboard');
        }, 1500);
      }
    } catch (err: any) {
      console.error('Subscription error:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already subscribed')) {
        // Already subscribed, redirect to dashboard
        localStorage.setItem('userEmail', formData.email);
        onClose();
        navigate('/app/reminders/dashboard');
      } else {
        setError(t('reminders.errors.subscriptionFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '60vh',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <NotificationsIcon sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h5">
              {t('reminders.title')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {showLogin ? (
          // Login Form
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('reminders.login.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('reminders.login.description')}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleLogin}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label={t('reminders.login.email')}
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  required
                  label={t('reminders.login.pin')}
                  type="text"
                  inputProps={{ maxLength: 4, pattern: '[0-9]{4}' }}
                  value={loginData.pin}
                  onChange={(e) => setLoginData({ ...loginData, pin: e.target.value })}
                  disabled={loading}
                  helperText={t('reminders.login.pinHelper')}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={24} /> : t('reminders.login.submit')}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('reminders.login.noAccount')}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() => setShowLogin(false)}
                  >
                    {t('reminders.subscription.subscribe')}
                  </Button>
                </Box>
              </Box>
            </form>
          </Box>
        ) : (
          // Subscription Form
          <Box>
            <Typography variant="h6" gutterBottom>
              {t('reminders.subscription.title')}
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{t('reminders.subscription.free')}:</strong> {t('reminders.subscription.freeLimit')}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>{t('reminders.subscription.premium')}:</strong> {t('reminders.subscription.premiumBenefit')}
              </Typography>
            </Alert>
            
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="body2" color="text.secondary">
                {t('reminders.login.alreadySubscribed')}
              </Typography>
              <Button
                variant="text"
                onClick={() => setShowLogin(true)}
              >
                {t('reminders.login.loginHere')}
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('reminders.messages.subscribeSuccess')}
              </Alert>
            )}

            <form onSubmit={handleSubscribe}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  required
                  label={t('reminders.subscription.email')}
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label={t('reminders.subscription.name')}
                  value={formData.pilot_name}
                  onChange={handleChange('pilot_name')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label={t('reminders.subscription.phone')}
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {t('reminders.notifications.reminderIntervals')}:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • {t('reminders.notifications.30days')}<br />
                    • {t('reminders.notifications.14days')}<br />
                    • {t('reminders.notifications.7days')}<br />
                    • {t('reminders.notifications.1day')}
                  </Typography>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {loading ? (
                    <CircularProgress size={24} />
                  ) : (
                    t('reminders.subscription.subscribe')
                  )}
                </Button>
              </Box>
            </form>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RemindersModal;